import React, { useEffect, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { isPromptOfType, promptOptionLabel, promptsOfType } from "../../helpers/promptTypes";

const SandboxModal = ({ open, onClose, whatsapp }) => {
  const [nombre, setNombre] = useState("Cliente de prueba");
  const [numero, setNumero] = useState("593999000111");
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);

  const [prompts, setPrompts] = useState([]);
  const [promptId, setPromptId] = useState("");
  const [guardandoPrompt, setGuardandoPrompt] = useState(false);

  useEffect(() => {
    if (!open) return;

    setPromptId(whatsapp?.promptId ? String(whatsapp.promptId) : "");

    (async () => {
      try {
        const { data } = await api.get("/prompt", { params: { pageNumber: "1" } });
        setPrompts(data.prompts || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open, whatsapp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardarPrompt = async (nuevoPromptId) => {
    if (!whatsapp?.id) return;

    setPromptId(nuevoPromptId);
    setGuardandoPrompt(true);

    try {
      await api.put(`/whatsapp/${whatsapp.id}`, {
        name: whatsapp.name,
        queueIds: (whatsapp.queues || []).map((q) => q.id),
        promptId: nuevoPromptId ? Number(nuevoPromptId) : null
      });
      toast.success("Prompt actualizado");
    } catch (err) {
      toastError(err);
    } finally {
      setGuardandoPrompt(false);
    }
  };

  const enviar = async (e) => {
    e.preventDefault();

    if (!texto.trim() || !whatsapp?.id) return;

    setEnviando(true);

    try {
      const { data } = await api.post(
        `/whatsapp/sandbox/${whatsapp.id}/entrada`,
        { nombre, numero, texto }
      );

      setTexto("");
      toast.success(`Mensaje entregado al ticket #${data.ticketId}`);
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={enviar}>
        <DialogTitle>Simular mensaje del cliente</DialogTitle>

        <DialogContent dividers>
          <DialogContentText style={{ marginBottom: 16, fontSize: 13.5 }}>
            Escribe como si fueras el cliente. Entra por{" "}
            <b>{whatsapp?.name}</b> y sigue el mismo camino que un mensaje real:
            cola, chatbot y avisos.
          </DialogContentText>

          <TextField
            select
            label="Prompt a probar"
            value={promptId}
            onChange={(e) => guardarPrompt(e.target.value)}
            variant="outlined"
            fullWidth
            margin="dense"
            disabled={guardandoPrompt}
            InputLabelProps={{ shrink: true }}
            slotProps={{ select: { native: true } }}
            helperText={
              !promptId
                ? "Sin un Prompt elegido, esta conexión nunca va a contestar."
                : "Se guarda solo, apenas lo cambiás."
            }
          >
            <option value="">— Elegí un Prompt —</option>
            {promptsOfType(prompts, "text", promptId).map((p) => (
              <option key={p.id} value={p.id} disabled={!isPromptOfType(p, "text")}>
                {promptOptionLabel(p, "text")}
              </option>
            ))}
          </TextField>

          <Divider style={{ margin: "16px 0" }} />

          <TextField
            label="Nombre del cliente"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            variant="outlined"
            fullWidth
            margin="dense"
          />

          <TextField
            label="Número"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            variant="outlined"
            fullWidth
            margin="dense"
            helperText="Es de mentira. Mantén el mismo para seguir la misma conversación."
          />

          <TextField
            label="Mensaje"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            variant="outlined"
            fullWidth
            margin="dense"
            multiline
            rows={3}
            autoFocus
          />

          <div
            style={{
              marginTop: 12,
              padding: "9px 12px",
              borderRadius: 8,
              fontSize: 12.5,
              lineHeight: 1.4,
              backgroundColor: "#fdf3e3",
              color: "#7a6023",
              border: "1px solid #e3cfa0",
            }}
          >
            Los tickets que salgan de aquí son reales: aparecen en la bandeja y
            cuentan en los informes. Ciérralos al terminar.
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary" disabled={enviando}>
            Cerrar
          </Button>
          <ButtonWithSpinner
            type="submit"
            variant="contained"
            color="primary"
            loading={enviando}
            disabled={!texto.trim()}
          >
            Enviar
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SandboxModal;
